import React, { useState } from 'react';
import { PieChart, BarChart2, TrendingUp, TrendingDown, Clipboard, Download } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент для отображения детализации доходов и расходов
 */
const IncomeExpenseBreakdown = ({ results, formData, incomeCategories, expenseCategories }) => {
  const [activeTab, setActiveTab] = useState('expenses'); // 'expenses' или 'income'

  if (!results) return null;

  // Функция для получения человекочитаемого имени категории
  const getCategoryName = (categoryId, isIncome) => {
    const categories = isIncome ? incomeCategories : expenseCategories;
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : categoryId;
  };

  // Функция получения цвета для категории
  const getCategoryColor = (categoryId, isIncome) => {
    // Палитра цветов для графиков
    const colors = [
      'bg-blue-500 dark:bg-blue-600',
      'bg-emerald-500 dark:bg-emerald-600',
      'bg-amber-500 dark:bg-amber-600',
      'bg-red-500 dark:bg-red-600',
      'bg-purple-500 dark:bg-purple-600',
      'bg-indigo-500 dark:bg-indigo-600',
      'bg-pink-500 dark:bg-pink-600',
      'bg-cyan-500 dark:bg-cyan-600',
      'bg-lime-500 dark:bg-lime-600',
      'bg-orange-500 dark:bg-orange-600',
      'bg-teal-500 dark:bg-teal-600',
      'bg-gray-500 dark:bg-gray-600'
    ];
    
    // Для постоянства цветов категорий
    const categoryMap = {};
    
    if (isIncome) {
      categoryMap.salary = colors[0];
      categoryMap.business = colors[1];
      categoryMap.side_job = colors[2];
      categoryMap.rental = colors[3];
      categoryMap.investments = colors[4];
      categoryMap.pension = colors[5];
      categoryMap.benefits = colors[6];
      categoryMap.other = colors[11];
    } else {
      categoryMap.housing = colors[0];
      categoryMap.utilities = colors[1];
      categoryMap.groceries = colors[2];
      categoryMap.health = colors[3];
      categoryMap.transport = colors[4];
      categoryMap.communication = colors[5];
      categoryMap.clothing = colors[6];
      categoryMap.entertainment = colors[7];
      categoryMap.education = colors[8];
      categoryMap.restaurants = colors[9];
      categoryMap.subscriptions = colors[10];
      categoryMap.other = colors[11];
    }
    
    return categoryMap[categoryId] || colors[11]; // Возвращаем серый если не найдено
  };

  // Подготовка данных для визуализации доходов по категориям
  const prepareIncomeData = () => {
    const data = [];
    
    // Группируем доходы по категориям
    Object.entries(results.incomesByCategory).forEach(([categoryId, amount]) => {
      if (amount > 0) {
        data.push({
          id: categoryId,
          name: getCategoryName(categoryId, true),
          amount,
          percentage: (amount / results.totalIncome * 100).toFixed(1)
        });
      }
    });
    
    // Сортируем по убыванию суммы
    return data.sort((a, b) => b.amount - a.amount);
  };

  // Подготовка данных для визуализации расходов по категориям
  const prepareExpenseData = () => {
    const data = [];
    
    // Группируем расходы по категориям
    Object.entries(results.expensesByCategory).forEach(([categoryId, amount]) => {
      if (amount > 0) {
        const category = expenseCategories.find(c => c.id === categoryId);
        data.push({
          id: categoryId,
          name: getCategoryName(categoryId, false),
          amount,
          percentage: (amount / results.totalExpenses * 100).toFixed(1),
          essential: category ? category.essential : false
        });
      }
    });
    
    // Сортируем по убыванию суммы
    return data.sort((a, b) => b.amount - a.amount);
  };

  // Данные для отображения
  const incomeData = prepareIncomeData();
  const expenseData = prepareExpenseData();

  // Экспорт данных в CSV
  const exportToCSV = () => {
    const incomeCsv = [
      ['Категория', 'Сумма', 'Процент от общего дохода'],
      ...incomeData.map(item => [
        item.name,
        item.amount.toFixed(2).replace('.', ','),
        `${item.percentage}%`
      ]),
      ['ИТОГО', results.totalIncome.toFixed(2).replace('.', ','), '100%']
    ];

    const expenseCsv = [
      ['Категория', 'Сумма', 'Процент от общих расходов', 'Тип'],
      ...expenseData.map(item => [
        item.name,
        item.amount.toFixed(2).replace('.', ','),
        `${item.percentage}%`,
        item.essential ? 'Обязательный' : 'Необязательный'
      ]),
      ['ИТОГО', results.totalExpenses.toFixed(2).replace('.', ','), '100%', '']
    ];

    // Соединяем все данные
    const csvContent = [
      ['АНАЛИЗ БЮДЖЕТА'],
      [''],
      ['ДОХОДЫ'],
      ...incomeCsv,
      [''],
      ['РАСХОДЫ'],
      ...expenseCsv
    ].map(row => row.join(';')).join('\n');

    // UTF-8 BOM для правильной кодировки в Excel
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'анализ_бюджета.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-5 mt-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-indigo-500" />
          Структура бюджета
        </h2>
        <div>
          <Button
            type="button"
            onClick={exportToCSV}
            icon={<Download className="w-4 h-4" />}
            variant="outline"
            size="sm"
          >
            Экспорт
          </Button>
        </div>
      </div>

      {/* Вкладки */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('expenses')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'expenses'
              ? 'text-red-600 dark:text-red-400 border-b-2 border-red-500 dark:border-red-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <div className="flex items-center">
            <TrendingDown className="w-4 h-4 mr-1.5" />
            Расходы ({expenseData.length})
          </div>
        </button>
        <button
          onClick={() => setActiveTab('income')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'income'
              ? 'text-emerald-600 dark:text-emerald-400 border-b-2 border-emerald-500 dark:border-emerald-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <div className="flex items-center">
            <TrendingUp className="w-4 h-4 mr-1.5" />
            Доходы ({incomeData.length})
          </div>
        </button>
      </div>

      {/* Основной контент */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Пирог */}
        <div className="md:w-2/5 mb-4 md:mb-0">
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 h-full">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <PieChart className="w-4 h-4 mr-1.5" />
              {activeTab === 'expenses' ? 'Структура расходов' : 'Структура доходов'}
            </h3>
            
            {/* Визуализация распределения */}
            <div className="flex items-center justify-center mb-5 h-48">
              <div className="relative rounded-full w-40 h-40">
                {/* Визуализируем как круговую диаграмму из секторов */}
                {(activeTab === 'expenses' ? expenseData : incomeData).map((item, index, arr) => {
                  // Рассчитываем углы для сектора на основе процентного соотношения
                  const total = arr.reduce((sum, i) => sum + parseFloat(i.percentage), 0);
                  const angle = (parseFloat(item.percentage) / total) * 360;
                  const prevAngles = arr.slice(0, index).reduce((sum, i) => sum + (parseFloat(i.percentage) / total) * 360, 0);
                  
                  return (
                    <div 
                      key={item.id}
                      className={`absolute inset-0 ${getCategoryColor(item.id, activeTab === 'income')}`}
                      style={{
                        clipPath: `polygon(50% 50%, 50% 0%, ${
                          50 + 50 * Math.cos((prevAngles + angle) * Math.PI / 180)
                        }% ${
                          50 - 50 * Math.sin((prevAngles + angle) * Math.PI / 180)
                        }%, ${
                          50 + 50 * Math.cos(prevAngles * Math.PI / 180)
                        }% ${
                          50 - 50 * Math.sin(prevAngles * Math.PI / 180)
                        }%)`
                      }}
                    ></div>
                  );
                })}
                
                {/* Центральный круг для создания эффекта пончика */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white dark:bg-gray-800 rounded-full"></div>
              </div>
            </div>

            {/* Сумма */}
            <div className="text-center mb-4">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {activeTab === 'expenses' ? 'Общие расходы' : 'Общие доходы'}
              </div>
              <div className={`text-xl font-bold ${
                activeTab === 'expenses' 
                  ? 'text-red-600 dark:text-red-400' 
                  : 'text-emerald-600 dark:text-emerald-400'
              }`}>
                {formatCurrency(activeTab === 'expenses' ? results.totalExpenses : results.totalIncome)} ₽
              </div>
            </div>
          </div>
        </div>

        {/* Список */}
        <div className="md:w-3/5">
          <div className="max-h-96 overflow-y-auto pr-1">
            {(activeTab === 'expenses' ? expenseData : incomeData).map((item) => (
              <div 
                key={item.id}
                className="mb-3 p-3 bg-gray-50 dark:bg-gray-800/70 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center">
                    {/* Цветовой индикатор */}
                    <div className={`w-3 h-3 rounded-full ${getCategoryColor(item.id, activeTab === 'income')} mr-2`}></div>
                    <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {item.name}
                    </h4>
                    {activeTab === 'expenses' && item.essential && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">
                        Обязательный
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {item.percentage}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="relative w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mr-3">
                    <div 
                      className={`absolute top-0 left-0 h-full ${getCategoryColor(item.id, activeTab === 'income')}`}
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold flex-shrink-0 text-gray-900 dark:text-white">
                    {formatCurrency(item.amount)} ₽
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomeExpenseBreakdown;